import type { CollectiblesConfig } from '../config/collectibles.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { TrackPresenter } from '../level/TrackPresenter';
import type { EntityVisual } from '../rendering/EntityVisual';
import type { VisualPool } from '../rendering/VisualPool';
import { clamp, DEG_TO_RAD, lerp, type Vec3 } from '../utils/math';
import type { CollectibleInstance } from './CollectibleInstance';

interface Entry {
  readonly item: CollectibleInstance;
  readonly visual: EntityVisual;
  readonly phase: number;
}

/** A bottle pulled in by a magnet on its way to the runner (world space, detached from its segment). */
interface Flight {
  readonly visual: EntityVisual;
  readonly from: Vec3;
  readonly phase: number;
  /** Render time of the first flight frame (NaN until then). */
  start: number;
}

/** Below this height a collectible stands on the road (on its base) and does not bob. */
const GROUNDED_HEIGHT = 0.05;

/** Pooled collectible visuals with a light spin/bob animation (visual only). */
export class CollectiblePresenter {
  private readonly entries = new Map<number, Entry>();
  private readonly flights: Flight[] = [];

  /** `runnerPosition`: world position of the runner's feet – target of magnet flights. */
  constructor(
    private readonly pool: VisualPool,
    private readonly track: TrackPresenter,
    private readonly config: CollectiblesConfig,
    events: EventBus<GameEvents>,
    private readonly runnerPosition: () => Readonly<Vec3> | null = () => null,
  ) {
    events.on('collectible:spawned', ({ collectible }) => this.show(collectible));
    events.on('collectible:despawned', ({ collectible }) => this.hide(collectible));
    events.on('collectible:collected', ({ collectible, attracted }) => (attracted ? this.fly(collectible) : this.hide(collectible)));
  }

  render(time: number): void {
    const spin = this.config.spinDegreesPerSecond * DEG_TO_RAD;
    for (const { item, visual, phase } of this.entries.values()) {
      if (item.definition.spins) visual.root.rotation.y = time * spin + phase;
      if (item.localPosition.y < GROUNDED_HEIGHT) continue; // stands on its base – no bobbing
      visual.root.position.y =
        item.localPosition.y + Math.sin(time * this.config.bobFrequency * Math.PI * 2 + phase) * this.config.bobAmplitude;
    }
    this.renderFlights(time, spin);
  }

  private show(item: CollectibleInstance): void {
    const visual = this.pool.acquire(item.definition.visualAssetId, this.track.rootOf(item.segment));
    // Path coordinates → on curves the item sits on the arc (bobbing only changes y).
    const p = item.segment.placement(item.localPosition);
    visual.root.position.set(p.x, p.y, p.z);
    visual.root.rotation.y = p.yaw;
    visual.root.scaling.setAll(item.scale); // the pool resets it on the next acquire
    // Phase by position: neighbouring bottles move as a gentle wave.
    this.entries.set(item.uid, { item, visual, phase: item.localPosition.z * 0.35 });
  }

  private hide(item: CollectibleInstance): void {
    const entry = this.entries.get(item.uid);
    if (!entry) return;
    this.pool.release(entry.visual);
    this.entries.delete(item.uid);
  }

  /** Magnet pickup: the visual leaves its segment and flies into the runner (it already counted). */
  private fly(item: CollectibleInstance): void {
    const entry = this.entries.get(item.uid);
    if (!entry) return;
    this.entries.delete(item.uid);
    const root = entry.visual.root;
    root.computeWorldMatrix(true);
    const p = root.getAbsolutePosition();
    const from = { x: p.x, y: p.y, z: p.z };
    // Detached: a recycled segment root can no longer take the bottle with it.
    root.parent = null;
    root.position.set(from.x, from.y, from.z);
    this.flights.push({ visual: entry.visual, from, phase: entry.phase, start: Number.NaN });
  }

  private renderFlights(time: number, spin: number): void {
    if (this.flights.length === 0) return;
    const target = this.runnerPosition();
    const { seconds, targetHeight, endScale } = this.config.magnetFlight;
    for (let i = this.flights.length - 1; i >= 0; i--) {
      const flight = this.flights[i]!;
      if (Number.isNaN(flight.start)) flight.start = time;
      const t = seconds > 0 ? clamp((time - flight.start) / seconds, 0, 1) : 1;
      if (t >= 1 || !target) {
        this.pool.release(flight.visual);
        this.flights.splice(i, 1);
        continue;
      }
      // Ease-in: starts gently, then snaps into the runner (who keeps running – the target moves along).
      const k = t * t;
      const root = flight.visual.root;
      root.position.set(lerp(flight.from.x, target.x, k), lerp(flight.from.y, target.y + targetHeight, k), lerp(flight.from.z, target.z, k));
      root.scaling.setAll(lerp(1, endScale, k));
      root.rotation.y = time * spin + flight.phase;
    }
  }
}
